import React, { useEffect, useRef, useState } from 'react';

interface ColumnFilterDropdownProps {
  columnTitle: string;
  uniqueValues: string[];
  selectedValues: string[];
  onFilterChange: (columnTitle: string, values: string[]) => void;
  isActive: boolean;
}

const IconFunnel = ({ active }: { active: boolean }) => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill={active ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3" />
  </svg>
)

export const ColumnFilterDropdown: React.FC<ColumnFilterDropdownProps> = ({
  columnTitle,
  uniqueValues,
  selectedValues,
  onFilterChange,
  isActive,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [filterSearch, setFilterSearch] = useState('');
  const [alignRight, setAlignRight] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Close on click outside
  useEffect(() => {
    if (!isOpen) return;
    const handleMouseDown = (e: MouseEvent) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setFilterSearch('');
      }
    };
    document.addEventListener('mousedown', handleMouseDown);
    return () => document.removeEventListener('mousedown', handleMouseDown);
  }, [isOpen]);

  // Right-align detection for edge columns
  useEffect(() => {
    if (!isOpen || !dropdownRef.current) return;
    const rect = dropdownRef.current.getBoundingClientRect();
    setAlignRight(rect.right > window.innerWidth - 8);
  }, [isOpen]);

  const toggleDropdown = (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setIsOpen(prev => !prev);
    setFilterSearch('');
  };

  const filteredValues = uniqueValues.filter(v =>
    v.toLowerCase().includes(filterSearch.toLowerCase())
  );

  const handleToggleValue = (value: string) => {
    const next = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onFilterChange(columnTitle, next);
  };

  const handleSelectAll = () => {
    if (filterSearch) {
      // Union current selection with visible (searched) values
      const merged = new Set(selectedValues);
      filteredValues.forEach(v => merged.add(v));
      onFilterChange(columnTitle, Array.from(merged));
    } else {
      onFilterChange(columnTitle, [...uniqueValues]);
    }
  };

  const handleClearAll = () => {
    if (filterSearch) {
      // Subtract only visible (searched) values from current selection
      const visibleSet = new Set(filteredValues);
      onFilterChange(columnTitle, selectedValues.filter(v => !visibleSet.has(v)));
    } else {
      onFilterChange(columnTitle, []);
    }
  };

  return (
    <div className="column-filter" ref={wrapperRef}>
      <button
        type="button"
        className={`column-filter__trigger${isActive ? ' column-filter__trigger--active' : ''}`}
        onClick={toggleDropdown}
        title={`Filter ${columnTitle}`}
      >
        <IconFunnel active={isActive} />
      </button>

      {isOpen && (
        <div className={`column-filter__dropdown${alignRight ? ' column-filter__dropdown--right' : ''}`} ref={dropdownRef}>
          <div className="column-filter__search">
            <input
              type="text"
              placeholder="Search..."
              value={filterSearch}
              onChange={e => setFilterSearch(e.target.value)}
              onClick={e => e.stopPropagation()}
              autoFocus
            />
          </div>
          <div className="column-filter__actions">
            <button type="button" onClick={e => { e.stopPropagation(); handleSelectAll(); }}>Select All</button>
            <button type="button" onClick={e => { e.stopPropagation(); handleClearAll(); }}>Clear All</button>
          </div>
          <div className="column-filter__options">
            {filteredValues.map(value => (
              <label key={value} onClick={e => e.stopPropagation()}>
                <input
                  type="checkbox"
                  checked={selectedValues.includes(value)}
                  onChange={e => { e.stopPropagation(); handleToggleValue(value); }}
                />
                <span style={value === '(Blanks)' ? { fontStyle: 'italic', opacity: 0.6 } : undefined}>{value}</span>
              </label>
            ))}
            {filteredValues.length === 0 && (
              <div className="column-filter__no-results">No matches</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
